import { HTMLAttributes, Key, memo, useEffect, useState } from 'react';

import Autocomplete, {
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGetKaapClusters } from '../../../hooks';
import { Cluster } from '../../../../types';
import { KaapClusterNodeGroupValue } from '../types';

const CLUSTER_GROUP_NAMES: Record<string, string> = {
  'dev-playground': 'Dev Playground - Experimental',
  'dev-internal': 'Dev Internal - For development environments',
  'prod-internal': 'Prod Internal - For internal production services',
  'prod-external':
    'Prod External - Only for production applications serving TomTom commercial clients',
};

const getClusterGroupName = (stage?: string, tier?: string): string => {
  if (!stage || !tier) {
    return tier ?? '';
  }
  return CLUSTER_GROUP_NAMES[`${stage}-${tier}`] ?? tier;
};

const capitalizeFirstLetter = (value: string) =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

const clusterLabel = (multiple: boolean) => (multiple ? 'Clusters' : 'Cluster');

// Every node group of any selected cluster can be chosen. Each cluster only
// receives the chosen ones it actually has (see `emit`).
const getAvailableNodeGroups = (clusters: Cluster[]): string[] =>
  Array.from(
    new Set(
      clusters.flatMap(cluster =>
        cluster.nodeGroups.filter(ng => {
          return ![
            'shared-general',
            'shared-storage',
            'shared-memory',
            'shared-compute',
          ].includes(ng);
        }),
      ),
    ),
  ).sort();

type ClusterSectionProps = {
  value: KaapClusterNodeGroupValue;
  stage: string;
  onChange: (value: KaapClusterNodeGroupValue) => void;
  disabled?: boolean;
  /** Allows picking several clusters of the same tier. One cluster otherwise. */
  multiple?: boolean;
};

export const ClusterSection = memo(
  ({
    value,
    stage,
    onChange,
    disabled,
    multiple = false,
  }: ClusterSectionProps) => {
    const { value: clusters, loading } = useGetKaapClusters();

    const stageClusters = (clusters ?? []).filter(
      c => !stage || c.stage === stage,
    );

    const selectedRefs = value?.clusters ?? [];
    const selectedClusters = selectedRefs.flatMap(
      ref => stageClusters.find(c => c.id === ref.id) ?? [],
    );

    // With several clusters allowed, the first pick fixes the tier; it stays
    // fixed until the last cluster is removed, at which point every cluster is
    // offered again. A single cluster can simply be replaced by another tier's.
    const tier = selectedClusters[0]?.tier ?? value?.tier ?? '';
    const options = stageClusters.filter(
      c => !multiple || !tier || c.tier === tier,
    );

    // The field is only flagged once the user has been in it and left it empty,
    // so the form opens without any error.
    const [touched, setTouched] = useState(false);

    const [selectedNodeGroups, setSelectedNodeGroups] = useState<string[]>(
      value?.nodeGroups ?? [],
    );

    useEffect(() => {
      if (
        !loading &&
        selectedRefs.length > 0 &&
        selectedClusters.length !== selectedRefs.length
      ) {
        setSelectedNodeGroups([]);
        onChange({ clusters: [], tier: '', nodeGroups: [] });
      }
    }, [stage, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const emit = (picked: Cluster[], nodeGroups: string[]) =>
      onChange({
        clusters: picked.map(({ id, name, nodeGroups: available }) => ({
          id,
          name,
          nodeGroups: nodeGroups.filter(nodeGroup =>
            available.includes(nodeGroup),
          ),
        })),
        tier: picked[0]?.tier ?? '',
        nodeGroups,
      });

    const handleClusterChange = (picked: Cluster[]) => {
      const available = getAvailableNodeGroups(picked);
      const nodeGroups = selectedNodeGroups.filter(nodeGroup =>
        available.includes(nodeGroup),
      );
      setSelectedNodeGroups(nodeGroups);
      emit(picked, nodeGroups);
    };

    const handleNodeGroupsChange = (_: any, nodeGroups: string[]) => {
      setSelectedNodeGroups(nodeGroups);
      if (selectedClusters.length > 0) {
        emit(selectedClusters, nodeGroups);
      }
    };

    useEffect(() => {
      if (value?.nodeGroups) {
        setSelectedNodeGroups(value.nodeGroups);
      }
    }, [selectedRefs.length, value?.nodeGroups]); // eslint-disable-line react-hooks/exhaustive-deps

    const availableNodeGroups = getAvailableNodeGroups(selectedClusters);

    const nodeGroupsControl = (
      <FormControl fullWidth variant="standard">
        <Autocomplete
          id="cluster-nodegroups"
          multiple
          size="small"
          value={selectedNodeGroups}
          options={availableNodeGroups}
          getOptionLabel={option => option}
          onChange={handleNodeGroupsChange}
          renderInput={params => (
            <TextField
              {...params}
              label="Dedicated Node Groups"
              size="small"
              variant="outlined"
            />
          )}
          noOptionsText="No node groups available for the selected clusters"
        />
        {selectedClusters.length > 0 && (
          <FormHelperText>
            Optional — leave empty to use only shared node groups
            {selectedClusters.length > 1 &&
              '. A node group is only applied to the selected clusters that have it'}
          </FormHelperText>
        )}
      </FormControl>
    );

    if (disabled) {
      const clusterIds = selectedRefs.map(ref => ref.id).join(', ');
      const tierLabel = capitalizeFirstLetter(tier);

      return (
        <Box
          sx={{ display: 'flex', flexDirection: 'column' }}
          style={{ gap: 16 }}
        >
          <Typography variant="h6">
            {selectedRefs.length > 1
              ? 'Clusters and Node Groups'
              : 'Cluster and Node Groups'}
          </Typography>
          <FormControl fullWidth variant="standard">
            <TextField
              id="cluster-tier"
              label="Tier"
              size="small"
              variant="standard"
              value={tierLabel}
              disabled
              slotProps={{ htmlInput: { readOnly: true } }}
            />
          </FormControl>
          <FormControl fullWidth variant="standard">
            <TextField
              id="cluster"
              label={clusterLabel(selectedRefs.length > 1)}
              size="small"
              variant="outlined"
              value={clusterIds}
              disabled
              slotProps={{ htmlInput: { readOnly: true } }}
            />
          </FormControl>
          {nodeGroupsControl}
        </Box>
      );
    }

    const hasClusters = selectedRefs.length > 0;
    const showError = touched && !hasClusters;
    const label = clusterLabel(multiple);

    const sortedOptions = [...options].sort(
      (a, b) => b.tier.localeCompare(a.tier) || b.name.localeCompare(a.name),
    );

    const sharedProps = {
      id: 'cluster',
      size: 'small' as const,
      loading,
      options: sortedOptions,
      groupBy: (option: Cluster) =>
        getClusterGroupName(option.stage, option.tier),
      getOptionLabel: (option: Cluster) => option.id,
      isOptionEqualToValue: (option: Cluster, val: Cluster) =>
        option.id === val.id,
      onBlur: () => setTouched(true),
      renderOption: (
        { key, ...optionProps }: HTMLAttributes<HTMLLIElement> & { key?: Key },
        option: Cluster,
      ) => (
        <li key={key} {...optionProps}>
          <Box>
            <Typography variant="body2">{option.id}</Typography>
          </Box>
        </li>
      ),
      renderInput: (params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          size="small"
          // With `multiple` the text input stays empty even when chips are
          // selected, so a permanent `required` would make the browser's own
          // validation block the Review button.
          required={!hasClusters}
          slotProps={{ inputLabel: { required: true } }}
          variant="outlined"
          error={showError}
        />
      ),
      noOptionsText: stage
        ? `No clusters found for stage "${stage}"`
        : 'No clusters found',
      loadingText: 'Loading clusters...',
    };

    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column' }}
        style={{ gap: 16 }}
      >
        <Typography variant="h6">
          {multiple ? 'Clusters and Node Groups' : 'Cluster and Node Groups'}
        </Typography>
        <FormControl fullWidth variant="standard" required error={showError}>
          {multiple ? (
            <Autocomplete
              {...sharedProps}
              multiple
              filterSelectedOptions
              value={selectedClusters}
              onChange={(_, picked) => handleClusterChange(picked)}
            />
          ) : (
            <Autocomplete
              {...sharedProps}
              value={selectedClusters[0] ?? null}
              onChange={(_, picked) =>
                handleClusterChange(picked ? [picked] : [])
              }
            />
          )}
          {multiple && (
            <FormHelperText>
              {tier
                ? `Showing only ${tier} clusters, because all clusters of a tenant must be in the same tier. Remove every selected cluster to choose from other tiers.`
                : 'Select one or more clusters. All clusters of a tenant must be in the same tier.'}
            </FormHelperText>
          )}
        </FormControl>

        {hasClusters && nodeGroupsControl}
      </Box>
    );
  },
);
